/**
 * Flights Popups Slice
 *
 * Centralized popup state management for all flights templates.
 * Handles error popups from API failures across:
 * - Interstitial (fare selection)
 * - Review Traveller (traveller form)
 * - Addons (meals, baggage, seats, coupons)
 * - Prebook
 *
 * Structure:
 * - currentPopup: key of currently shown popup (null = no popup)
 * - popupMap: map of popup key to popup data
 */
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import {
  fetchFlightInterstitialThunk,
  fetchInterstitialWithFareThunk,
} from '../thunks/interstitialThunks';
import { fetchReviewTravellerThunk } from '../thunks/reviewTravellerThunks';
import { prebookFlightThunk } from '../thunks/prebookThunks';

// ============================================
// Popup Constants
// ============================================

/** Key for API error popup */
export const API_ERROR_POPUP_KEY = 'API_ERROR';

// ============================================
// Popup Types
// ============================================

/**
 * Error details for popup display
 */
export interface PopupErrorDetails {
  errorMessage: string;
  errorDescription?: string;
}

/**
 * Popup state structure
 */
export interface FlightsPopupsState {
  /** Currently active popup key (null = no popup shown) */
  currentPopup: string | null;
  /** Map of popup key to popup data */
  popupMap: Record<string, PopupErrorDetails>;
}

// ============================================
// Initial State
// ============================================

const initialPopupsState: FlightsPopupsState = {
  currentPopup: null,
  popupMap: {},
};

// ============================================
// Helper Functions
// ============================================

/**
 * Check if payload contains error details structure
 */
function isErrorDetailsPayload(payload: unknown): payload is PopupErrorDetails {
  return (
    payload !== null &&
    typeof payload === 'object' &&
    'errorMessage' in payload &&
    typeof (payload as PopupErrorDetails).errorMessage === 'string'
  );
}

/**
 * Handle rejected thunk with error details
 */
function handleRejectedWithErrorDetails(state: FlightsPopupsState, payload: unknown): void {
  if (isErrorDetailsPayload(payload)) {
    state.popupMap[API_ERROR_POPUP_KEY] = {
      errorMessage: payload.errorMessage,
      ...(payload.errorDescription ? { errorDescription: payload.errorDescription } : {}),
    };
    state.currentPopup = API_ERROR_POPUP_KEY;
  }
}

// ============================================
// Slice
// ============================================

const popupsSlice = createSlice({
  name: 'flights/popups',
  initialState: initialPopupsState,
  reducers: {
    /** Set current popup to show */
    setFlightsCurrentPopup: (state, action: PayloadAction<string | null>) => {
      state.currentPopup = action.payload;
    },

    /** Set popup data for a specific key */
    setFlightsPopupData: (
      state,
      action: PayloadAction<{ key: string; data: PopupErrorDetails }>,
    ) => {
      state.popupMap[action.payload.key] = action.payload.data;
    },

    /** Show error popup with data */
    showFlightsErrorPopup: (state, action: PayloadAction<PopupErrorDetails>) => {
      state.popupMap[API_ERROR_POPUP_KEY] = action.payload;
      state.currentPopup = API_ERROR_POPUP_KEY;
    },

    /** Clear current popup (close popup) */
    clearFlightsPopup: (state) => {
      state.currentPopup = null;
    },

    /** Clear all popups (current popup and popup map) */
    clearAllFlightsPopups: (state) => {
      state.currentPopup = null;
      state.popupMap = {};
    },

    /** Reset popups state to initial values */
    resetFlightsPopupsState: () => initialPopupsState,
  },
  extraReducers: (builder) => {
    // ============================================
    // Interstitial Thunks
    // ============================================

    // Fetch Flight Interstitial - clear popup on success, show on error
    builder
      .addCase(fetchFlightInterstitialThunk.fulfilled, (state) => {
        state.currentPopup = null;
      })
      .addCase(fetchFlightInterstitialThunk.rejected, (state, action) => {
        handleRejectedWithErrorDetails(state, action.payload);
      });

    // Fetch Interstitial With Fare - clear popup on success, show on error
    builder
      .addCase(fetchInterstitialWithFareThunk.fulfilled, (state) => {
        state.currentPopup = null;
      })
      .addCase(fetchInterstitialWithFareThunk.rejected, (state, action) => {
        handleRejectedWithErrorDetails(state, action.payload);
      });

    // ============================================
    // Review Traveller Thunks
    // ============================================

    // Fetch Review Traveller - clear popup on success, show on error
    builder
      .addCase(fetchReviewTravellerThunk.fulfilled, (state) => {
        state.currentPopup = null;
      })
      .addCase(fetchReviewTravellerThunk.rejected, (state, action) => {
        handleRejectedWithErrorDetails(state, action.payload);
      });

    // ============================================
    // Prebook Thunks
    // ============================================

    // Prebook Flight - clear popup on success, show on error
    builder
      .addCase(prebookFlightThunk.fulfilled, (state) => {
        state.currentPopup = null;
      })
      .addCase(prebookFlightThunk.rejected, (state, action) => {
        handleRejectedWithErrorDetails(state, action.payload);
      });
  },
});

// ============================================
// Actions
// ============================================

export const {
  setFlightsCurrentPopup,
  setFlightsPopupData,
  showFlightsErrorPopup,
  clearFlightsPopup,
  clearAllFlightsPopups,
  resetFlightsPopupsState,
} = popupsSlice.actions;

// ============================================
// Reducer Export
// ============================================

export const popupsReducer = popupsSlice.reducer;
